/**
 * CLDR plural-form selector.
 *
 * The single i18n primitive shared across every client surface's locale
 * catalog. Each locale's count-bearing message is authored as a
 * `PluralForms` bag (`{ one, other, … }`) and rendered by calling
 * `selectPlural(locale, count, forms)` — the branch that used to be
 * baked into the English-only `count === 1 ? … : …` ternary now lives
 * inside `Intl.PluralRules`, so locales that need `few` / `many` / `two`
 * (ru, pl, ar, …) express them by supplying those keys.
 *
 * NO side effects, NO platform globals — `Intl` is a language built-in,
 * so this file stays as testable and host-agnostic as the rest of
 * `@reflecto/client-core`.
 */

/**
 * Per-CLDR-category strings for a single count-bearing message.
 *
 * `other` is mandatory (every locale has it, and it doubles as the
 * fallback for categories the caller didn't supply). The rest are
 * optional — a caller only lists the categories its locale actually
 * distinguishes.
 */
export type PluralForms = {
  zero?: string;
  one?: string;
  two?: string;
  few?: string;
  many?: string;
  other: string;
};

/**
 * Pick the CLDR plural form for `count` in `locale` via
 * `Intl.PluralRules`, falling back to `other` for categories the caller
 * didn't supply.
 *
 * Degrades to `other` if `Intl.PluralRules` rejects `locale`: a
 * well-formed but unknown tag (`"xx"`) resolves normally, but a
 * malformed tag (`"en-!!"`, `""`) throws `RangeError` at construction —
 * catching it keeps a bad locale value from crashing the render.
 *
 * @param locale BCP 47 language tag (e.g. `"en"`, `"es-MX"`, `"ru"`).
 * @param count  The quantity the message is agreeing with.
 * @param forms  Per-category strings; `other` is required.
 */
export function selectPlural(locale: string, count: number, forms: PluralForms): string {
  let category: Intl.LDMLPluralRule = "other";
  try {
    category = new Intl.PluralRules(locale).select(count);
  } catch {
    // Malformed locale tag — degrade to `other` rather than throw.
    category = "other";
  }
  return forms[category] ?? forms.other;
}
